import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { RequestExportBatch } from '@magimp/shared';

import { ApiClientError } from '../../../api/errors.js';
import { downloadRequestsBatchXlsx } from '../../../api/requests/index.js';
import { Banner, Button, EditorModal } from '../../../ui/index.js';
import { triggerBrowserDownload } from './requestDrawerDownload.js';
import type { RequestsBatchExportController } from '../selection/useRequestSelection.js';

interface RequestsExportDialogProps {
  open: boolean;
  requestIds: readonly string[];
  onClose: () => void;
}

function stringArray(value: unknown): string[] | null {
  if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) return null;
  return value;
}

export function RequestsExportDialog({ open, requestIds, onClose }: RequestsExportDialogProps) {
  const { t } = useTranslation(['requests', 'common']);
  const [includeRateColumns, setIncludeRateColumns] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setIncludeRateColumns(false);
    setError(null);
  }, [open]);

  const formatError = (value: unknown): string => {
    if (value instanceof ApiClientError && value.httpStatus === 404) {
      const missing = stringArray(value.meta?.['requestIds']);
      if (missing && missing.length > 0) {
        return t('requests:export.bulk.missing', { ids: missing.join(', ') });
      }
    }
    if (value instanceof ApiClientError && value.httpStatus === 413) {
      const requestId = value.meta?.['requestId'];
      const cap = value.meta?.['cap'];
      if (typeof requestId === 'string' && typeof cap === 'string') {
        return t('requests:export.bulk.oversized', { id: requestId });
      }
    }
    return t('requests:export.error');
  };

  const exportRequests = async (): Promise<void> => {
    if (exporting || requestIds.length === 0) return;
    setExporting(true);
    setError(null);
    const input: RequestExportBatch = { requestIds: [...requestIds], includeRateColumns };
    try {
      const { blob, filename } = await downloadRequestsBatchXlsx(input);
      triggerBrowserDownload(blob, filename ?? 'requests.xlsx');
      onClose();
    } catch (value) {
      setError(formatError(value));
    } finally {
      setExporting(false);
    }
  };

  return (
    <EditorModal
      open={open}
      dirty={false}
      busy={exporting}
      onConfirmClose={onClose}
      title={t('requests:export.bulk.title')}
      width={420}
      footer={
        <>
          <Button kind="ghost" disabled={exporting} onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            kind="primary"
            disabled={exporting || requestIds.length === 0}
            onClick={() => void exportRequests()}
          >
            {exporting ? t('requests:export.downloading') : t('requests:export.bulk.confirm')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-5)' }}>
        <div style={{ color: 'var(--fg-2)', fontSize: 'var(--fz-13)' }}>
          {t('requests:export.bulk.selectedCount', { count: requestIds.length })}
        </div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-3)',
            color: 'var(--fg-1)',
            fontSize: 'var(--fz-13)',
            cursor: exporting ? 'not-allowed' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={includeRateColumns}
            disabled={exporting}
            onChange={(event) => setIncludeRateColumns(event.target.checked)}
            style={{
              width: 'var(--s-5)',
              height: 'var(--s-5)',
              margin: 0,
              accentColor: 'var(--accent-1)',
            }}
          />
          {t('requests:export.bulk.includeRateColumns')}
        </label>
        {error && <Banner tone="error">{error}</Banner>}
      </div>
    </EditorModal>
  );
}

export function RequestsBatchExportDialog({
  controller,
}: {
  controller: Pick<
    RequestsBatchExportController,
    'exportDialogOpen' | 'selectedIds' | 'closeExport'
  >;
}) {
  if (!controller.exportDialogOpen) return null;
  return (
    <RequestsExportDialog
      open
      requestIds={controller.selectedIds}
      onClose={controller.closeExport}
    />
  );
}
